// frontend/src/features/campaign/components/common/CampaignDateRangePicker.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  parseLocalDate,
  formatLocalDate,
  addDays,
  calculateDiffDays,
  formatDisplayDate,
} from '../../utils/dateUtils';
import './CampaignDateRangePicker.css';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const DURATION_PRESETS = [
  { label: '14 Days', days: 14 },
  { label: '30 Days', days: 30 },
  { label: '45 Days', days: 45 },
  { label: '60 Days', days: 60 },
  { label: '90 Days', days: 90 },
];

export default function CampaignDateRangePicker({
  startDate = '2026-09-08',
  endDate = '2026-10-23',
  durationDays = 45,
  disabled = false,
  onChange,
}) {
  // Today string for comparison
  const todayStr = useMemo(() => formatLocalDate(new Date()), []);

  // Calendar view navigation (Year and Month of the single displayed month)
  const [viewYear, setViewYear] = useState(() => {
    const dt = parseLocalDate(startDate) || new Date();
    return dt.getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    const dt = parseLocalDate(startDate) || new Date();
    return dt.getMonth();
  });

  // Range selection in progress
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  // Sync calendar view month when startDate changes externally (e.g. playbook selection)
  useEffect(() => {
    const dt = parseLocalDate(startDate);
    if (dt) {
      setViewYear(dt.getFullYear());
      setViewMonth(dt.getMonth());
    }
  }, [startDate]);

  // Navigate month
  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleJumpToToday = () => {
    const dt = new Date();
    setViewYear(dt.getFullYear());
    setViewMonth(dt.getMonth());
  };

  // ── Handling Duration Changes (Typing in number box or clicking preset) ──
  const handleDurationChange = useCallback(
    (newDays) => {
      const baseStart = startDate || todayStr;

      // If user completely clears the input (empty string)
      if (newDays === '' || newDays === null || newDays === undefined) {
        if (onChange) {
          onChange({
            startDate: baseStart,
            endDate: '',
            durationDays: '',
          });
        }
        return;
      }

      const days = parseInt(newDays, 10);
      if (isNaN(days) || days < 0) return;

      const newEnd = days === 0 ? baseStart : addDays(baseStart, days);

      // Ensure calendar is looking at the start month
      const dt = parseLocalDate(baseStart);
      if (dt) {
        setViewYear(dt.getFullYear());
        setViewMonth(dt.getMonth());
      }
      setIsSelectingEnd(false);

      if (onChange) {
        onChange({
          startDate: baseStart,
          endDate: newEnd,
          durationDays: days,
        });
      }
    },
    [startDate, todayStr, onChange]
  );

  // ── Handling Direct Start Date Input ──
  const handleStartDateInputChange = (e) => {
    const newStart = e.target.value;
    if (!newStart) return;

    const numDuration =
      durationDays === '' || durationDays === null || durationDays === undefined
        ? 0
        : parseInt(durationDays, 10);
    const validDuration = isNaN(numDuration) || numDuration < 0 ? 0 : numDuration;
    const newEnd = validDuration > 0 ? addDays(newStart, validDuration) : newStart;

    const dt = parseLocalDate(newStart);
    if (dt) {
      setViewYear(dt.getFullYear());
      setViewMonth(dt.getMonth());
    }

    if (onChange) {
      onChange({
        startDate: newStart,
        endDate: newEnd,
        durationDays: durationDays,
      });
    }
  };

  // ── Handling Direct End Date Input ──
  const handleEndDateInputChange = (e) => {
    const newEnd = e.target.value;
    if (!newEnd) return;

    const baseStart = startDate || todayStr;
    const calculatedDuration = calculateDiffDays(baseStart, newEnd);

    if (onChange) {
      onChange({
        startDate: baseStart,
        endDate: newEnd,
        durationDays: calculatedDuration >= 0 ? calculatedDuration : 0,
      });
    }
  };

  // ── Handling Calendar Date Clicks ──
  const handleDayClick = (dateStr) => {
    if (disabled) return;
    if (!isSelectingEnd) {
      // First click: start picking range
      setIsSelectingEnd(true);
      setHoverDate(null);
      if (onChange) {
        onChange({
          startDate: dateStr,
          endDate: '',
          durationDays: 1,
        });
      }
    } else {
      // Second click: end picking range
      if (dateStr >= startDate) {
        const diff = calculateDiffDays(startDate, dateStr);
        setIsSelectingEnd(false);
        setHoverDate(null);
        if (onChange) {
          onChange({
            startDate,
            endDate: dateStr,
            durationDays: diff,
          });
        }
      } else {
        // User clicked before startDate -> restart selection from clicked day
        setHoverDate(null);
        if (onChange) {
          onChange({
            startDate: dateStr,
            endDate: '',
            durationDays: 1,
          });
        }
      }
    }
  };

  const handleDayHover = (dateStr) => {
    if (disabled) return;
    if (isSelectingEnd) {
      setHoverDate(dateStr);
    }
  };

  // Determine active effective end date (for hover preview and display)
  const computedEnd = useMemo(() => {
    if (endDate) return endDate;
    if (startDate && Number(durationDays) > 0) {
      return addDays(startDate, Number(durationDays));
    }
    return '';
  }, [startDate, endDate, durationDays]);

  const effectiveEndDate = useMemo(() => {
    if (isSelectingEnd && hoverDate && hoverDate >= startDate) {
      return hoverDate;
    }
    return computedEnd;
  }, [isSelectingEnd, hoverDate, startDate, computedEnd]);

  // Helper to generate days for the single displayed month
  const renderMonthGrid = (year, monthIdx) => {
    const totalDays = new Date(year, monthIdx + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, monthIdx, 1).getDay(); // 0 is Sunday
    const days = [];

    // Empty lead slots
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ key: `blank-${monthIdx}-${i}`, isBlank: true });
    }

    // Actual day cells
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isStart = dateStr === startDate;
      const isEnd = dateStr === effectiveEndDate;
      const isInRange =
        startDate && effectiveEndDate && dateStr > startDate && dateStr < effectiveEndDate;
      const isToday = dateStr === todayStr;

      days.push({
        key: dateStr,
        dayNum: d,
        dateStr,
        isStart,
        isEnd,
        isInRange,
        isToday,
      });
    }

    return (
      <div className="cmp-cal-month-card">
        <div className="cmp-cal-weekdays">
          {WEEKDAY_NAMES.map((w) => (
            <span key={w} className="cmp-cal-weekday">
              {w}
            </span>
          ))}
        </div>
        <div className="cmp-cal-days-grid">
          {days.map((cell) => {
            if (cell.isBlank) {
              return <div key={cell.key} className="cmp-cal-day blank" />;
            }

            let cellClasses = 'cmp-cal-day';
            if (cell.isStart) cellClasses += ' is-start';
            if (cell.isEnd) cellClasses += ' is-end';
            if (cell.isInRange) cellClasses += ' in-range';
            if (cell.isToday) cellClasses += ' is-today';
            if (disabled) cellClasses += ' disabled';

            return (
              <button
                key={cell.key}
                type="button"
                className={cellClasses}
                disabled={disabled}
                onClick={() => handleDayClick(cell.dateStr)}
                onMouseEnter={() => handleDayHover(cell.dateStr)}
                title={cell.dateStr}
              >
                <span className="cmp-cal-day-number">{cell.dayNum}</span>
                {cell.isToday && <span className="cmp-cal-today-dot" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`cmp-date-duration-picker-container ${disabled ? 'disabled-view' : ''}`}>
      {/* ── LEFT PANE: Start, End, Duration, Presets & Summary ── */}
      <div className="cmp-picker-left-pane">
        <div className="cmp-pane-heading">
          <i className="fas fa-calendar-alt text-primary" /> Campaign Timeline & Schedule{' '}
          <span className="text-danger">*</span>
        </div>

        {/* Direct Start & End Date Inputs Row */}
        <div className="cmp-dates-row">
          {/* Start Date Field */}
          <div className="cmp-dp-input-block">
            <label className="cmp-dp-label">
              <i className="fas fa-calendar-day" /> Start Date{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="cmp-field-input cmp-dp-input"
              value={startDate}
              disabled={disabled}
              onChange={handleStartDateInputChange}
            />
          </div>

          {/* End Date Field */}
          <div className="cmp-dp-input-block">
            <label className="cmp-dp-label">
              <i className="fas fa-calendar-check" /> End Date{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="cmp-field-input cmp-dp-input"
              value={computedEnd}
              disabled={disabled}
              onChange={handleEndDateInputChange}
            />
          </div>
        </div>

        {/* Duration Date Number Box */}
        <div className="cmp-dp-input-block duration-box-block">
          <label className="cmp-dp-label">
            <i className="fas fa-hourglass-half" /> Campaign Duration (Days){' '}
            <span className="text-danger">*</span>
          </label>
          <div className="cmp-duration-number-wrap">
            <input
              type="number"
              min="0"
              max="365"
              className="cmp-field-input cmp-duration-number-input"
              disabled={disabled}
              value={
                durationDays === '' || durationDays === null || durationDays === undefined
                  ? ''
                  : durationDays
              }
              onChange={(e) => handleDurationChange(e.target.value)}
              placeholder="e.g. 30"
            />
            <span className="cmp-duration-unit-badge">Days</span>
          </div>
        </div>

        {/* Quick Duration Preset Chips */}
        <div className="cmp-duration-presets-bar">
          <span className="cmp-duration-presets-label">
            <i className="fas fa-bolt text-warning" /> Quick Presets:
          </span>
          <div className="cmp-duration-chips">
            {DURATION_PRESETS.map((p) => {
              const isPresetActive = Number(durationDays) === p.days;
              return (
                <button
                  key={p.days}
                  type="button"
                  className={`cmp-duration-chip-btn ${isPresetActive ? 'active' : ''}`}
                  disabled={disabled}
                  onClick={() => handleDurationChange(p.days)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Summary Card */}
        <div className="cmp-timeline-summary-card">
          <div className="cmp-cal-summary-pill">
            <i className="fas fa-check-circle text-success" />
            <span>
              <strong>{formatDisplayDate(startDate)}</strong> →{' '}
              <strong>{formatDisplayDate(endDate)}</strong>
            </span>
          </div>
          <span className="cmp-cal-duration-badge">
            <strong>{durationDays === '' ? 0 : durationDays || 0}</strong> Days
          </span>
        </div>

        {/* Helpful Tip */}
        <div className="cmp-cal-hint">
          <i className="fas fa-info-circle" />{' '}
          {isSelectingEnd
            ? 'Click a second date on the calendar to finish the range.'
            : 'Click start & end dates on the calendar, or type days directly.'}
        </div>
      </div>

      {/* ── RIGHT PANE: Single-Month Interactive Calendar ── */}
      <div className="cmp-picker-right-pane">
        <div className="cmp-calendar-wrapper">
          {/* Calendar Nav Bar with Month/Year Title */}
          <div className="cmp-calendar-nav-bar">
            <button
              type="button"
              className="cmp-cal-nav-btn"
              onClick={handlePrevMonth}
              title="Previous Month"
            >
              <i className="fas fa-chevron-left" />
            </button>

            <div className="cmp-cal-nav-center">
              <span className="cmp-cal-month-title">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button
                type="button"
                className="cmp-cal-today-btn"
                onClick={handleJumpToToday}
                title="Jump to Current Month"
              >
                Today
              </button>
            </div>

            <button
              type="button"
              className="cmp-cal-nav-btn"
              onClick={handleNextMonth}
              title="Next Month"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>

          {/* Single Month Grid View */}
          <div className="cmp-calendar-single-view">{renderMonthGrid(viewYear, viewMonth)}</div>
        </div>
      </div>
    </div>
  );
}
